import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import clsx from 'clsx';
import GenericModal from '@components/common/modals/GenericModal';
import Button from '@components/common/Button';
import Select from '@components/common/Select';
import { Input } from '@components/common';
import { supplierService } from '@services';
import { useDebounce } from '@hooks/useDebounce';
import type { Supplier, SupplierTier } from '@app-types';
import styles from './SupplierSearchModal.module.css';

interface SupplierSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentSupplierId?: number;
  onSupplierSelect: (supplier: Supplier) => void;
}

const SupplierSearchModal: React.FC<SupplierSearchModalProps> = ({
  isOpen,
  onClose,
  currentSupplierId,
  onSupplierSelect
}) => {
  // Search filters
  const [supplierNumber, setSupplierNumber] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');

  // Search results
  const [searchResults, setSearchResults] = useState<Supplier[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Sorting state
  const [sortField, setSortField] = useState<'name' | 'vendor_id' | 'tier_name'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Debounce search inputs (500ms delay)
  const debouncedSupplierNumber = useDebounce(supplierNumber, 500);
  const debouncedNameFilter = useDebounce(nameFilter, 500);
  const debouncedTierFilter = useDebounce(tierFilter, 500);

  // Fetch tiers for dropdown
  const { data: tiersData } = useQuery({
    queryKey: ['supplierTiers'],
    queryFn: () => supplierService.getSupplierTiers()
  });

  const tiers = tiersData || [];

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSupplierNumber('');
      setTierFilter('');
      setNameFilter('');
      setSearchResults([]);
    }
  }, [isOpen]);

  // Auto-search when debounced values change
  useEffect(() => {
    const performSearch = async () => {
      // Only search if at least one filter has a value
      if (!debouncedSupplierNumber && !debouncedNameFilter && !debouncedTierFilter) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);

      try {
        // Send both name and vendorId to the server so filtering happens server-side
        // (AND-combined, case-insensitive). No client-side filtering against a capped page.
        const response = await supplierService.getSuppliers({
          name: debouncedNameFilter || undefined,
          vendorId: debouncedSupplierNumber || undefined,
          tierId: debouncedTierFilter ? Number.parseInt(debouncedTierFilter, 10) : undefined,
          limit: 100,
          offset: 0
        });

        setSearchResults(response.suppliers || []);
      } catch (error) {
        console.error('Error searching suppliers:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    if (isOpen) {
      performSearch();
    }
  }, [debouncedSupplierNumber, debouncedNameFilter, debouncedTierFilter, isOpen]);

  const handleClearFilters = () => {
    setSupplierNumber('');
    setTierFilter('');
    setNameFilter('');
    setSearchResults([]);
  };

  const handleSelectSupplier = (supplier: Supplier) => {
    onSupplierSelect(supplier);
  };

  // Handle column header click for sorting
  const handleSort = (field: 'name' | 'vendor_id' | 'tier_name') => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get sort indicator icon
  const getSortIcon = (field: 'name' | 'vendor_id' | 'tier_name') => {
    if (sortField !== field) {
      return <ArrowUpDown size={14} className={clsx(styles.sortIcon, styles.sortIconInactive)} />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp size={14} className={clsx(styles.sortIcon, styles.sortIconActive)} />
    ) : (
      <ArrowDown size={14} className={clsx(styles.sortIcon, styles.sortIconActive)} />
    );
  };

  // Sort results based on current sort configuration
  const sortedResults = useMemo(() => {
    const sorted = [...searchResults].sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';

      // Convert to strings for comparison
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [searchResults, sortField, sortDirection]);

  // Tier options for dropdown
  const tierOptions = [
    { value: '', label: 'All Tiers' },
    ...tiers.map((tier: SupplierTier) => ({
      value: tier.id.toString(),
      label: tier.name
    }))
  ];

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title="Search Suppliers"
      size="lg"
      dataTestId="supplier-search-modal"
    >
      {/* Search Form */}
      <div className={styles.searchForm}>
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="supplierNumber">Supplier Number</label>
            <Input
              id="supplierNumber"
              type="text"
              value={supplierNumber}
              onChange={(e) => setSupplierNumber(e.target.value)}
              placeholder="Enter supplier number..."
              dataTestId="supplier-search-number"
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="tier">Tier</label>
            <Select options={tierOptions} value={tierFilter} onChange={setTierFilter} placeholder="All Tiers" />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="nameFilter">Supplier Name</label>
            <Input
              id="nameFilter"
              type="text"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              placeholder="Enter supplier name..."
              dataTestId="supplier-search-name"
            />
          </div>
        </div>

        {(supplierNumber || tierFilter || nameFilter) && (
          <div className={styles.searchActions}>
            <Button variant="secondary" onClick={handleClearFilters}>
              <X size={16} />
              Clear Filters
            </Button>
          </div>
        )}
      </div>

      {/* Search Results */}
      {(supplierNumber || tierFilter || nameFilter) && (
        <div className={styles.searchResults}>
          {isSearching && (
            <div className={styles.searchingIndicator}>
              <p>Searching...</p>
            </div>
          )}
          {!isSearching && sortedResults.length > 0 && (
            <>
              <p className={styles.resultsCount}>
                Found {sortedResults.length} supplier{sortedResults.length !== 1 ? 's' : ''}
              </p>
              <div className={styles.resultsTable}>
                <table>
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('vendor_id')} className={styles.sortableHeader}>
                        <span className={styles.headerContent}>
                          Supplier Number
                          {getSortIcon('vendor_id')}
                        </span>
                      </th>
                      <th onClick={() => handleSort('name')} className={styles.sortableHeader}>
                        <span className={styles.headerContent}>
                          Supplier Name
                          {getSortIcon('name')}
                        </span>
                      </th>
                      <th onClick={() => handleSort('tier_name')} className={styles.sortableHeader}>
                        <span className={styles.headerContent}>
                          Tier
                          {getSortIcon('tier_name')}
                        </span>
                      </th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedResults.map((supplier) => {
                      const isCurrentSupplier = supplier.id === currentSupplierId;
                      return (
                        <tr
                          key={supplier.id}
                          className={clsx(isCurrentSupplier && styles.currentSupplier)}
                          data-test-id="supplier-search-result-row"
                        >
                          <td>{supplier.vendor_id || '-'}</td>
                          <td>{supplier.name}</td>
                          <td>{supplier.tier_name || '-'}</td>
                          <td>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleSelectSupplier(supplier)}
                              disabled={isCurrentSupplier}
                              dataTestId="supplier-search-select-btn"
                            >
                              {isCurrentSupplier ? 'Current' : 'Select'}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {!isSearching && sortedResults.length === 0 && (
            <div className={styles.noResults}>
              <p>No suppliers found matching your search criteria.</p>
              <p className={styles.noResultsHint}>Try adjusting your filters.</p>
            </div>
          )}
        </div>
      )}
    </GenericModal>
  );
};

export default SupplierSearchModal;
